<template>
    <article v-show="visible" :id="anchorId" class="ds-entry" :class="wide && 'ds-entry-wide'">
        <header class="ds-entry-header">
            <div class="flex items-baseline justify-between gap-3">
                <h3 class="title-body-bold text-ink">{{ name }}</h3>
                <a :href="`#${anchorId}`" class="ds-entry-anchor" :aria-label="`Link to ${name}`">#</a>
            </div>
            <template v-if="description">
                <p :id="descriptionId" class="body-small text-ink-muted" :class="isLongDescription && !isDescriptionExpanded && 'line-clamp-2'">{{ description }}</p>
                <button
                    v-if="isLongDescription"
                    type="button"
                    class="ds-entry-more"
                    :aria-expanded="isDescriptionExpanded"
                    :aria-controls="descriptionId"
                    @click="isDescriptionExpanded = !isDescriptionExpanded">
                    {{ isDescriptionExpanded ? "Show less" : "Show more" }}
                </button>
            </template>
        </header>
        <div class="ds-entry-demo">
            <slot />
        </div>
    </article>
</template>

<script lang="ts">
interface DesignSystemEntryProps {
    name: string;
    description?: string;
    // Extra search terms, for components whose common name differs from the component name.
    keywords?: string;
    // Span both columns, for demos that need the width.
    wide?: boolean;
}
</script>

<script setup lang="ts">
import { computed, inject, ref } from "vue";

import { designSystemQueryKey, designSystemSectionKey, matchesQuery, toAnchorId } from "./context";

const props = defineProps<DesignSystemEntryProps>();

const anchorId = computed(() => toAnchorId(props.name));
const descriptionId = computed(() => `${anchorId.value}-description`);

const query = inject(designSystemQueryKey, ref(""));
const visible = computed(() => matchesQuery(query.value, props.name, props.description, props.keywords));

inject(designSystemSectionKey, undefined)?.({ name: props.name, visible });

const DESCRIPTION_CLAMP_LENGTH = 180;
const isLongDescription = computed(() => (props.description?.length ?? 0) > DESCRIPTION_CLAMP_LENGTH);
const isDescriptionExpanded = ref(false);
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    .ds-entry {
        @apply mb-6 flex scroll-mt-32 break-inside-avoid flex-col overflow-hidden rounded-lgr border border-border bg-surface-raised;
    }

    .ds-entry-wide {
        column-span: all;
    }

    .ds-entry-header {
        @apply flex flex-col gap-1 border-b border-border bg-surface-sunken px-4 py-3;
    }

    .ds-entry-anchor {
        @apply shrink-0 text-ink-muted opacity-0 transition-opacity no-underline;
    }

    .ds-entry:hover .ds-entry-anchor,
    .ds-entry-anchor:focus-visible {
        @apply opacity-100;
    }

    .ds-entry-more {
        @apply w-fit scaffold-details-small text-ink-muted underline;
    }

    .ds-entry-demo {
        @apply p-4;
    }
}
</style>
